import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { ExportedFrame } from 'components/VisualStudioPlayground/VisualEditor/utils/export/exportDesign'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useMutation } from 'react-query'
import { MediaObject } from 'types'

type Props = {
  designId: string
}

type Data = Array<MediaObject>

type Variables = Array<ExportedFrame>

type MediaObjectPayload = {
  source_url: string
  mime_type: string
  frame_id: string
}

export const generatePresignedUrls = async (designId: string, frameIds: string[]) => {
  const res = await sendToApi<{ presigned_urls: string[] }>(`/almanac_files/${designId}/design/presigned_urls`, {
    method: 'POST',
    body: {
      frame_ids: frameIds,
    },
  })

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error('Failed to generate presigned URLS')
  }

  return res.data.presigned_urls
}

export const uploadFile = async (presignedUrl: string, file: File) => {
  const res = await fetch(presignedUrl, { method: 'PUT', body: file })
  if (!res.ok) {
    throw new Error('Failed to upload file')
  }
  return res
}

const createMediaObjects = async (workspaceId: number, designId: string, exportedFrames: ExportedFrame[]) => {
  const presignedUrls = await generatePresignedUrls(
    designId,
    exportedFrames.map(({ frameId }) => frameId),
  )

  await Promise.all(presignedUrls.map((presignedUrl, index) => uploadFile(presignedUrl, exportedFrames[index].file)))

  const mediaObjectsPayload = exportedFrames
    .map((frame) => {
      const index = exportedFrames.findIndex((f) => f.frameId === frame.frameId)
      if (frame.type === 'image') {
        return {
          source_url: presignedUrls[index].split('?')[0],
          mime_type: frame.file.type,
          frame_id: frame.frameId,
          design_key: frame.designKey,
        }
      } else if (frame.type === 'video') {
        return {
          source_url: frame.source_url,
          mime_type: frame.mime_type,
          frame_id: frame.frameId,
          thumbnail_url: presignedUrls[index].split('?')[0],
          duration: frame.duration,
          design_key: frame.designKey,
        }
      }
      return null
    })
    .filter(Boolean) as MediaObjectPayload[]

  const res = await sendToApi(`/w/${workspaceId}/media_objects/batch`, {
    method: 'POST',
    body: {
      media_objects: mediaObjectsPayload,
    },
  })

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(res.data)
  }

  return res.data.media_objects
}

export const useCreateDesignMediaObjects = ({ designId }: Props) => {
  const workspaceId = useCurrentWorkspaceId()
  return useMutation<Data, Error, Variables>((vars) => createMediaObjects(workspaceId, designId, vars))
}
